import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  pdvApi,
  personnelApi,
  trainingCertificatesApi,
  medicalRecordsApi,
  personnelReportsApi as reportsApi,
} from '../lib/api';
import { toast } from 'sonner';
import {
  Users,
  FileSpreadsheet,
  FileText,
  Download,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Mail,
  Award,
  Stethoscope,
  Building2,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const summaryStatus = (statuses) => {
  if (statuses.includes('expired')) return 'expired';
  if (statuses.includes('expiring_soon')) return 'expiring_soon';
  if (statuses.length === 0) return 'no_data';
  return 'valid';
};

const statusPill = (status) => {
  if (status === 'expired') return <Badge className="bg-red-100 text-red-800">🔴 Da rinnovare</Badge>;
  if (status === 'expiring_soon') return <Badge className="bg-amber-100 text-amber-800">🟡 In scadenza</Badge>;
  if (status === 'no_data') return <Badge className="bg-gray-100 text-gray-700">⚪ Nessun dato</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800">🟢 In regola</Badge>;
};

export default function EmployeesOverviewPage() {
  const [pdvs, setPdvs] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [certs, setCerts] = useState([]);
  const [medics, setMedics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdvFilter, setPdvFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [pdvRes, pRes, cRes, mRes] = await Promise.all([
        pdvApi.getAll(),
        personnelApi.getAll().catch(() => ({ data: [] })),
        trainingCertificatesApi.getAll().catch(() => ({ data: [] })),
        medicalRecordsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setPdvs(Array.isArray(pdvRes.data) ? pdvRes.data : pdvRes.data.items || []);
      setPersonnel(Array.isArray(pRes.data) ? pRes.data : pRes.data.items || []);
      setCerts(cRes.data || []);
      setMedics(mRes.data || []);
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Build aggregated view: per personnel, count certs/medics + status
  const rows = useMemo(() => {
    const pdvMap = Object.fromEntries(pdvs.map((p) => [p.id, p.name || p.company_name]));
    const result = personnel.map((p) => {
      const personCerts = certs.filter((c) => c.personnel_id === p.id);
      const personMedics = medics.filter((m) => m.personnel_id === p.id);
      const allStatuses = [
        ...personCerts.map((c) => c.status),
        ...personMedics.map((m) => m.status),
      ];
      const status = summaryStatus(allStatuses);
      const next_exp_dates = [
        ...personCerts.map((c) => c.expiry_date).filter(Boolean),
        ...personMedics.map((m) => m.next_visit_date).filter(Boolean),
      ];
      const next_exp = next_exp_dates.length ? next_exp_dates.sort()[0] : null;
      return {
        ...p,
        pdv_name: pdvMap[p.pdv_id] || '-',
        n_certs: personCerts.length,
        n_medics: personMedics.length,
        n_expired: allStatuses.filter((s) => s === 'expired').length,
        n_expiring: allStatuses.filter((s) => s === 'expiring_soon').length,
        next_expiry: next_exp,
        overall_status: status,
      };
    });
    return result;
  }, [personnel, certs, medics, pdvs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return rows.filter((r) => {
      if (pdvFilter !== 'all' && r.pdv_id !== pdvFilter) return false;
      if (q) {
        const hay = `${r.last_name || ''} ${r.first_name || ''} ${r.fiscal_code || ''} ${r.email || ''} ${r.pdv_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, pdvFilter, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    in_regola: rows.filter((r) => r.overall_status === 'valid').length,
    in_scadenza: rows.filter((r) => r.overall_status === 'expiring_soon').length,
    da_rinnovare: rows.filter((r) => r.overall_status === 'expired').length,
    nessun_dato: rows.filter((r) => r.overall_status === 'no_data').length,
  }), [rows]);

  const downloadReport = (params, format) => {
    window.open(reportsApi.getPersonnelSummaryUrl({ ...params, format }), '_blank');
  };

  const personnelName = (p) => `${p.last_name || ''} ${p.first_name || ''}`.trim();

  return (
    <div className="space-y-6" data-testid="employees-overview-page">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-600" />
            Riepilogo Dipendenti
          </h1>
          <p className="text-gray-600 mt-1">
            Vista unica con personale, attestati formazione e visite mediche per tutte le Unità Locali.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => downloadReport({}, 'xlsx')} data-testid="report-all-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel completo
          </Button>
          <Button variant="outline" onClick={() => downloadReport({}, 'pdf')} data-testid="report-all-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF completo
          </Button>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="py-4">
          <div className="text-xs text-gray-500 uppercase">Totale</div>
          <div className="text-2xl font-bold mt-1">{stats.total}</div>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50/30"><CardContent className="py-4">
          <div className="text-xs text-emerald-700 uppercase flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> In regola
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.in_regola}</div>
        </CardContent></Card>
        <Card className="border-amber-200 bg-amber-50/30"><CardContent className="py-4">
          <div className="text-xs text-amber-700 uppercase flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> In scadenza
          </div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{stats.in_scadenza}</div>
        </CardContent></Card>
        <Card className="border-red-200 bg-red-50/30"><CardContent className="py-4">
          <div className="text-xs text-red-700 uppercase flex items-center gap-1">
            <XCircle className="w-3 h-3" /> Da rinnovare
          </div>
          <div className="text-2xl font-bold text-red-700 mt-1">{stats.da_rinnovare}</div>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <div className="text-xs text-gray-500 uppercase">Nessun dato</div>
          <div className="text-2xl font-bold text-gray-700 mt-1">{stats.nessun_dato}</div>
        </CardContent></Card>
      </div>

      {/* Quick actions per UL */}
      {pdvs.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-blue-600" />
              <span className="font-medium">Report rapido per Unità Locale</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {pdvs.map((pdv) => (
                <div key={pdv.id} className="flex items-center justify-between gap-2 bg-gray-50 px-3 py-2 rounded">
                  <span className="text-sm truncate">{pdv.name || pdv.company_name}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" title="Excel"
                      onClick={() => downloadReport({ pdvId: pdv.id }, 'xlsx')}>
                      <FileSpreadsheet className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="ghost" title="PDF"
                      onClick={() => downloadReport({ pdvId: pdv.id }, 'pdf')}>
                      <FileText className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card><CardContent className="py-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Cerca dipendente</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
              <Input className="pl-8" placeholder="Nome, CF, email..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="min-w-[220px]">
            <Label className="text-xs">Unità Locale</Label>
            <Select value={pdvFilter} onValueChange={setPdvFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le UL</SelectItem>
                {pdvs.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name || p.company_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent></Card>

      {/* Table */}
      {loading ? <div className="text-center py-10 text-gray-500">Caricamento...</div>
        : filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-gray-500">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            Nessun dipendente trovato.
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dipendente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Unità Locale</TableHead>
                  <TableHead className="text-center"><Award className="w-4 h-4 inline" /> Cert.</TableHead>
                  <TableHead className="text-center"><Stethoscope className="w-4 h-4 inline" /> Visite</TableHead>
                  <TableHead className="text-center text-red-600">Scaduti</TableHead>
                  <TableHead className="text-center text-amber-600">In scad.</TableHead>
                  <TableHead>Prossima scadenza</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="font-medium">{personnelName(r) || r.fiscal_code}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.fiscal_code || ''}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.email ? (
                        <a href={`mailto:${r.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {r.email}
                        </a>
                      ) : <span className="text-gray-400">-</span>}
                    </TableCell>
                    <TableCell className="text-sm">{r.pdv_name}</TableCell>
                    <TableCell className="text-center font-medium">{r.n_certs}</TableCell>
                    <TableCell className="text-center font-medium">{r.n_medics}</TableCell>
                    <TableCell className="text-center font-bold text-red-600">{r.n_expired || ''}</TableCell>
                    <TableCell className="text-center font-bold text-amber-600">{r.n_expiring || ''}</TableCell>
                    <TableCell className="text-sm">{r.next_expiry || '-'}</TableCell>
                    <TableCell>{statusPill(r.overall_status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        )}

      {/* Tip */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3 flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <strong>Suggerimento:</strong> per inviare automaticamente le email di alert ai dipendenti
            con scadenze imminenti, vai su <strong>Scadenze Formazione</strong> → "Invia alert email".
            Quando aggiungi un nuovo dipendente, viene inviato un alert agli amministratori e RSPP.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
